import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import './Header.css';

interface HeaderProps {
  toggleNightMode: () => void;
  isNightMode: boolean;
}

type LangCode = 'en' | 'fr-FR' | 'nl';

const LANGUAGES: Array<{ code: LangCode; short: string; name: string }> = [
  { code: 'en', short: 'EN', name: 'English' },
  { code: 'fr-FR', short: 'FR', name: 'Français' },
  { code: 'nl', short: 'NL', name: 'Nederlands' },
];

const Header: React.FC<HeaderProps> = ({ toggleNightMode, isNightMode }) => {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement | null>(null);

  const currentLang =
    LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    if (!langOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const handleLanguageChange = (code: LangCode) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="hE">
      <div className="inner">
        <div className="left">
          <Link className="sh-logo" to="/dashboard" aria-label="Smarteaming">
            <img src="/logo192.png" alt="" className="logo-mark" />
            <span>Smarteaming</span>
          </Link>
        </div>

        <div className="right">
          <button
            type="button"
            className="sh-theme-icon"
            onClick={toggleNightMode}
            aria-label={isNightMode ? t('Light mode') : t('Dark mode')}
            title={isNightMode ? t('Light mode') : t('Dark mode')}
          >
            {/* Sun (shown in dark mode) */}
            <svg className="sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
            {/* Moon (shown in light mode) */}
            <svg className="moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
            </svg>
          </button>

          <div className={`lang-select ${langOpen ? 'open' : ''}`} ref={langRef}>
            <button
              type="button"
              className="lang-btn"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              aria-label={t('Language')}
              onClick={() => setLangOpen(o => !o)}
            >
              <span>{currentLang.short}</span>
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M2 4l4 4 4-4" />
              </svg>
            </button>
            <ul className="lang-menu" role="listbox" aria-label={t('Language')}>
              {LANGUAGES.map(lang => (
                <li
                  key={lang.code}
                  role="option"
                  aria-selected={lang.code === currentLang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <b>{lang.short}</b>
                  <small>{lang.name}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
